import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import DataTable from '../../components/ui/data-table';
import EmptyState from '../../components/ui/empty-state.jsx';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import RankingDisplay from '../../components/grades/RankingDisplay.jsx';
import ReportCardViewer from '../../components/grades/ReportCardViewer.jsx';
import { useGrades } from '../../hooks/useGrades';
import { useAuth } from '../../context/AuthContext.jsx';
import { reportsService } from '../../services/reportsService';
import { 
  Award, 
  TrendingUp, 
  Trophy, 
  FileText, 
  BarChart3,
  Target
} from 'lucide-react';

const GradesPage = () => {
  const { user } = useAuth();
  const { grades, loading, error, fetchGrades } = useGrades();
  
  const [selectedTerm, setSelectedTerm] = React.useState('current');
  const [reportCard, setReportCard] = React.useState(null);
  const [loadingReportCard, setLoadingReportCard] = React.useState(false);

  const columns = [
    { id: 'subject', header: 'Subject', accessorKey: 'subjectName', sortable: true },
    { id: 'assessment', header: 'Assessment', accessorKey: 'assessmentTitle' },
    { id: 'score', header: 'Score', accessorKey: 'score', sortable: true },
    { id: 'percentage', header: 'Percentage', accessorKey: 'percentage', sortable: true },
    { id: 'grade', header: 'Grade', accessorKey: 'grade', sortable: true },
    { id: 'date', header: 'Date', accessorKey: 'dateAwarded', sortable: true },
  ];

  // Calculate summary statistics
  const getSummaryStats = () => {
    if (!grades || grades.length === 0) return null;

    const totalGrades = grades.length;
    const averageGrade = grades.reduce((sum, grade) => sum + grade.percentage, 0) / totalGrades;
    const highestGrade = Math.max(...grades.map(g => g.percentage));
    const lowestGrade = Math.min(...grades.map(g => g.percentage));
    const passedSubjects = grades.filter(g => g.percentage >= 50).length;
    const failedSubjects = totalGrades - passedSubjects;

    return {
      totalGrades,
      averageGrade,
      highestGrade,
      lowestGrade,
      passedSubjects,
      failedSubjects,
      passRate: (passedSubjects / totalGrades) * 100
    };
  };

  const summaryStats = getSummaryStats();

  // Generate report card
  const handleGenerateReportCard = async () => {
    try {
      setLoadingReportCard(true);
      const response = await reportsService.generateReportCard(user.id, { term: selectedTerm });
      setReportCard(response.data?.data || response.data);
    } catch (error) {
      console.error('Error generating report card:', error);
      // Show error message to user
      alert(error.response?.data?.message || 'Failed to generate report card');
    } finally {
      setLoadingReportCard(false);
    }
  };

  // Download report card
  const handleDownloadReportCard = async () => {
    try {
      const response = await reportsService.downloadReportCard(user.id, { term: selectedTerm });
      // Create blob and download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report-card-${user.id}-${selectedTerm}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading report card:', error);
      alert(error.response?.data?.message || 'Failed to download report card');
    }
  };

  // Print report card
  const handlePrintReportCard = () => {
    window.print();
  };

  const getRoleSpecificTitle = () => {
    switch (user?.role) {
      case 'student':
        return 'My Grades & Performance';
      case 'parent':
        return 'Child\'s Grades & Performance';
      case 'teacher':
        return 'Student Grades';
      default:
        return 'Grades & Performance';
    }
  };

  const getRoleSpecificDescription = () => {
    switch (user?.role) {
      case 'student':
        return 'Track your academic performance, rankings, and progress';
      case 'parent':
        return 'Monitor your child\'s academic progress and performance';
      case 'teacher':
        return 'View and manage student grades and performance';
      default:
        return 'Academic performance and grades information';
    }
  };

  if (loading) return <LoadingSpinner />;
  
  if (error) return (
    <div className="p-6">
      <Card>
        <CardContent className="p-6">
          <p className="text-red-600 text-sm">{String(error)}</p>
          <Button className="mt-3" onClick={() => fetchGrades()}>Retry</Button>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">{getRoleSpecificTitle()}</h2>
          <p className="text-gray-600">{getRoleSpecificDescription()}</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant={selectedTerm === 'current' ? 'default' : 'outline'}
            size="sm"
            onClick={() => {
              setSelectedTerm('current');
              fetchGrades({ term: 'current' });
            }}
          >
            Current Term
          </Button>
          <Button
            variant={selectedTerm === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => {
              setSelectedTerm('all');
              fetchGrades({ term: 'all' });
            }}
          >
            All Terms
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleGenerateReportCard}
            disabled={loadingReportCard}
          >
            <FileText className="w-4 h-4 mr-2" />
            {loadingReportCard ? 'Generating...' : 'Generate Report Card'}
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      {summaryStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Award className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Average Grade</p>
                  <p className="text-2xl font-semibold">{summaryStats.averageGrade.toFixed(1)}%</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Pass Rate</p>
                  <p className="text-2xl font-semibold">{summaryStats.passRate.toFixed(1)}%</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Trophy className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Highest Grade</p>
                  <p className="text-2xl font-semibold">{summaryStats.highestGrade.toFixed(1)}%</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Target className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Subjects Passed</p>
                  <p className="text-2xl font-semibold">{summaryStats.passedSubjects}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <BarChart3 className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Grades</p>
                  <p className="text-2xl font-semibold">{summaryStats.totalGrades}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content Tabs */}
      <Tabs defaultValue="grades" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="grades">Grades</TabsTrigger>
          <TabsTrigger value="rankings">Rankings</TabsTrigger>
          <TabsTrigger value="report-card">Report Card</TabsTrigger>
        </TabsList>

        <TabsContent value="grades">
          {(!grades || grades.length === 0) ? (
            <EmptyState 
              icon={Award}
              title="No grades available"
              description="Your grades will appear here once published."
              action
              actionLabel="Refresh"
              onAction={() => fetchGrades()}
            />
          ) : (
            <Card>
              <CardContent className="p-0">
                <DataTable data={grades} columns={columns} searchable />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="rankings">
          <RankingDisplay 
            rankings={null}
            showHistory={true}
            historicalRankings={[]}
          />
        </TabsContent>

        <TabsContent value="report-card">
          <ReportCardViewer 
            reportCard={reportCard}
            onDownload={handleDownloadReportCard}
            onPrint={handlePrintReportCard}
            showPreview={true}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default GradesPage;


